import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getDatabase, ref as dbRef, set, get, push } from 'firebase/database';
import { PhotoModel } from '../types/protocols';
import { extractExifData, extractExifDataAlternative, getBestExifDate } from './ExifUtils';
import { formatDateForStorage } from './DateUtils';

export interface ImageUploadResult {
    success: boolean;
    photoModel?: PhotoModel;
    error?: string;
}

export interface ImageUploadOptions {
    buildingKey: string;
    componentKey: string;
    file: File;
    storagePath: string;
    author: string;
}

/**
 * Creates a database entry for a photo before uploading to Storage
 * This ensures the Firebase Function can find the database entry when it runs
 */
export const createPhotoDatabaseEntry = async (
    buildingKey: string,
    componentKey: string,
    photoKey: string,
    storagePath: string,
    author: string,
    file: File
): Promise<{ success: boolean; photoModel?: PhotoModel; error?: string }> => {
    try {
        // Extract EXIF data to get the best date
        let exifData;
        if (file.type === 'image/png' || file.name.toLowerCase().endsWith('.png')) {
            // PNG files can have EXIF data, try both methods
            exifData = await extractExifData(file);
            if (!exifData || Object.keys(exifData).length === 0) {
                exifData = await extractExifDataAlternative(file);
            }
        } else {
            exifData = await extractExifData(file);
        }
        const exifDate = getBestExifDate(exifData);
        const finalDate = exifDate || formatDateForStorage(new Date());
        
        // Get image dimensions
        const dimensions = await getImageDimensions(file);

        // Create PhotoModel
        const photoModel: PhotoModel = {
            key: photoKey,
            author: author,
            date: finalDate,
            width: dimensions.width,
            height: dimensions.height,
            storagePath: storagePath
        };

        // Create database entry first
        const database = getDatabase();
        const photoRef = dbRef(database, `buildings/${buildingKey}/structure/components/${componentKey}/photos/${photoKey}`);
        await set(photoRef, photoModel);
        
        return {
            success: true,
            photoModel
        };
    } catch (error) {
        console.error('Error creating photo database entry:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error occurred'
        };
    }
};

/**
 * Uploads an image file to Firebase Storage
 * The database entry should already exist when this is called
 */
export const uploadImageToStorage = async (options: ImageUploadOptions): Promise<ImageUploadResult> => {
    try {
        const { storagePath, file } = options;
        
        // Upload file to Firebase Storage
        const storage = getStorage();
        const fileRef = storageRef(storage, storagePath);
        const uploadResult = await uploadBytes(fileRef, file);
        
        // Get download URL
        const downloadURL = await getDownloadURL(uploadResult.ref);
        
        return {
            success: true
        };
    } catch (error) {
        console.error('Error uploading image to storage:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error occurred'
        };
    }
};

/**
 * Updates the building model to include new photos for a component
 */
export const updateBuildingWithNewPhotos = async (
    buildingKey: string,
    componentKey: string,
    newPhotos: PhotoModel[]
): Promise<{ success: boolean; error?: string }> => {
    try {
        const db = getDatabase();
        
        // Get current building data
        const buildingRef = dbRef(db, `buildings/${buildingKey}`);
        const buildingSnapshot = await get(buildingRef);
        
        if (!buildingSnapshot.exists()) {
            return { success: false, error: 'Building not found' };
        }
        
        const buildingData = buildingSnapshot.val();
        
        // Ensure structure and components exist
        if (!buildingData.structure) {
            buildingData.structure = {};
        }
        if (!buildingData.structure.components) {
            buildingData.structure.components = {};
        }
        if (!buildingData.structure.components[componentKey]) {
            return { success: false, error: 'Component not found' };
        }
        
        // Get existing photos or initialize empty object
        const component = buildingData.structure.components[componentKey];
        if (!component.photos) {
            component.photos = {};
        }
        
        // Add new photos
        newPhotos.forEach(photo => {
            component.photos[photo.key] = photo;
        });
        
        // Update the building data
        await set(buildingRef, buildingData);
        
        return { success: true };
    } catch (error) {
        console.error('Error updating building with new photos:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error occurred'
        };
    }
};

/**
 * Gets image dimensions from a file
 */
export const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
        if (file.type === 'image/png' || file.name.toLowerCase().endsWith('.png')) {
            // PNG files are supported natively by browsers
            const img = new Image();
            img.onload = () => {
                URL.revokeObjectURL(img.src);
                resolve({ width: img.naturalWidth, height: img.naturalHeight });
            };
            img.onerror = () => {
                URL.revokeObjectURL(img.src);
                reject(new Error('Failed to load PNG image'));
            };
            img.src = URL.createObjectURL(file);
        } else {
            // For other image formats (JPEG, etc.), use standard approach
            const img = new Image();
            img.onload = () => {
                URL.revokeObjectURL(img.src);
                resolve({ width: img.naturalWidth, height: img.naturalHeight });
            };
            img.onerror = () => {
                URL.revokeObjectURL(img.src);
                reject(new Error('Failed to load image'));
            };
            img.src = URL.createObjectURL(file);
        }
    });
};

/**
 * Generates a unique storage path for an image
 */
export const generateStoragePath = (
    buildingKey: string,
    componentKey: string,
    fileName: string,
    index: number = 0
): string => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const sanitizedFileName = fileName.replace(/[^a-zA-Z0-9.-]/g, '_');
    return `buildings/${buildingKey}/components/${componentKey}/photos/${timestamp}_${index}_${sanitizedFileName}`;
};

/**
 * Complete image upload process: create database entry, upload to storage, and update building model
 */
export const uploadImagesForComponent = async (
    buildingKey: string,
    componentKey: string,
    files: File[],
    author: string
): Promise<{ success: boolean; photoModels?: PhotoModel[]; error?: string }> => {
    try {
        const uploadPromises = files.map(async (file, index) => {
            // Generate a unique key using Firebase push
            const database = getDatabase();
            const photosRef = dbRef(database, `buildings/${buildingKey}/structure/components/${componentKey}/photos`);
            const newPhotoRef = push(photosRef);
            const photoKey = newPhotoRef.key!;
            
            const storagePath = `buildings/${buildingKey}/structure/components/${componentKey}/photos/${photoKey}`;
            
            // Step 1: Create database entry first (so Firebase Function can find it)
            const dbResult = await createPhotoDatabaseEntry(
                buildingKey,
                componentKey,
                photoKey,
                storagePath,
                author,
                file
            );
            
            if (!dbResult.success || !dbResult.photoModel) {
                throw new Error(dbResult.error || 'Failed to create database entry');
            }
            
            // Step 2: Upload to storage (this will trigger the Firebase Function)
            const uploadResult = await uploadImageToStorage({
                buildingKey,
                componentKey,
                file,
                storagePath,
                author
            });
            
            if (!uploadResult.success) {
                throw new Error(uploadResult.error || 'Upload to storage failed');
            }
            
            return dbResult.photoModel;
        });
        
        const photoModels = await Promise.all(uploadPromises);
        
        // Step 3: Update building model (this is now redundant since we created individual entries,
        // but keeping it for consistency with existing code)
        const updateResult = await updateBuildingWithNewPhotos(buildingKey, componentKey, photoModels);
        
        if (!updateResult.success) {
            console.warn('Failed to update building model, but individual photo entries were created:', updateResult.error);
            // Don't throw here since the main upload succeeded
        }
        
        return {
            success: true,
            photoModels
        };
    } catch (error) {
        console.error('Error in complete upload process:', error);
        return {
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error occurred'
        };
    }
};
